import 'react-datepicker/dist/react-datepicker.css';

import { Button, Typography } from '@breetometer/components';
import { classNameMerger } from '@breetometer/utils';
import { CalendarArrowIcon, CalendarIcon, ChevronDown, CloseModal } from 'BreetAssets';
import { dateConst } from 'BreetHelpers';
import { addMonths } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';

import { CustomHeader } from './CustomHeader';
import type { IDateRangePicker } from './DateRangePicker.helpers';
import { SegmentedDateInput } from './SegmentedDateInput';

const TIME_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);
const DEFAULT_START_HOUR = 0;
const DEFAULT_END_HOUR = 23;

const formatHourLabel = (hour: number): string => `${hour % 12 === 0 ? 12 : hour % 12} ${hour < 12 ? 'AM' : 'PM'}`;

const applyHourToDate = (date: Date, hour: number, boundary: 'start' | 'end'): Date => {
	const next = new Date(date);
	if (boundary === 'end') next.setHours(hour, 59, 59, 999);
	else next.setHours(hour, 0, 0, 0);
	return next;
};

const DateRangePicker = ({
	onApply,
	onClear,
	onClose,
	loading,
	initialStartDate = null,
	initialEndDate = null,
}: IDateRangePicker) => {
	const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([initialStartDate, initialEndDate]);
	const [calendarKey, setCalendarKey] = useState(0);
	const [externalSync, setExternalSync] = useState(0);
	const [startHour, setStartHour] = useState(DEFAULT_START_HOUR);
	const [endHour, setEndHour] = useState(DEFAULT_END_HOUR);
	const [timeTouched, setTimeTouched] = useState(false);
	const [showTimePanel, setShowTimePanel] = useState(false);
	const [activeBoundary, setActiveBoundary] = useState<'start' | 'end'>('start');
	const [scrollHint, setScrollHint] = useState<'down' | 'up' | null>(null);

	const [startDate, endDate] = dateRange;

	const validationError = useMemo(() => {
		if (!startDate || !endDate) return null;
		const now = new Date();
		if (startDate > now || endDate > now) return 'Dates cannot be in the future.';
		if (endDate < startDate) return 'The end date must be on or after the start date.';
		if (endDate > addMonths(startDate, 6)) return 'Date range cannot exceed 6 months.';
		return null;
	}, [startDate, endDate]);

	const canApply = Boolean(startDate && endDate) && !validationError;

	const dropdownRef = useRef<HTMLDivElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);

	const bumpSync = useCallback(() => setExternalSync((n) => n + 1), []);

	const handleRangeChange = useCallback(
		(update: [Date | null, Date | null]) => {
			setDateRange(update);
			bumpSync();
			if (update[0] && update[1]) {
				setCalendarKey((k) => k + 1);
			}
		},
		[bumpSync]
	);

	// Typed dates are stored as-is (whatever the user entered) and validated separately, so a
	// future / out-of-order / out-of-range entry surfaces an inline message instead of being
	// silently dropped or wiping the other field. The calendar only remounts when a full date is
	// committed — not on every keystroke — so it no longer flickers while typing.
	const handleStartDate = useCallback((date: Date | null) => {
		setDateRange(([, end]) => [date, end]);
		if (date) setCalendarKey((k) => k + 1);
	}, []);

	const handleEndDate = useCallback((date: Date | null) => {
		setDateRange(([start]) => [start, date]);
		if (date) setCalendarKey((k) => k + 1);
	}, []);

	const handleApply = useCallback(() => {
		if (!startDate || !endDate || validationError) return;

		const start = applyHourToDate(startDate, startHour, 'start');
		const end = applyHourToDate(endDate, endHour, 'end');

		onApply({
			startDate: start,
			endDate: end,
			utcStartDate: formatInTimeZone(start, 'UTC', dateConst.FULL_DATE_TIME_FORMAT_ISO_Z),
			utcEndDate: formatInTimeZone(end, 'UTC', dateConst.FULL_DATE_TIME_FORMAT_ISO_Z),
			hasTime: timeTouched,
		});
	}, [startDate, endDate, validationError, startHour, endHour, timeTouched, onApply]);

	const handleClear = useCallback(() => {
		setDateRange([null, null]);
		bumpSync();
		setStartHour(DEFAULT_START_HOUR);
		setEndHour(DEFAULT_END_HOUR);
		setTimeTouched(false);
		setShowTimePanel(false);
		onClear?.();
	}, [onClear, bumpSync]);

	const toggleTimePanel = useCallback(() => setShowTimePanel((open) => !open), []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setShowTimePanel(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const updateScrollHint = useCallback(() => {
		const el = calendarRef.current;
		if (!el || el.scrollHeight - el.clientHeight <= 4) {
			setScrollHint(null);
			return;
		}
		setScrollHint(el.scrollHeight - el.scrollTop - el.clientHeight > 4 ? 'down' : 'up');
	}, []);

	useEffect(() => {
		updateScrollHint();
		const el = calendarRef.current;
		if (!el) return;

		el.addEventListener('scroll', updateScrollHint, { passive: true });
		window.addEventListener('resize', updateScrollHint);

		return () => {
			el.removeEventListener('scroll', updateScrollHint);
			window.removeEventListener('resize', updateScrollHint);
		};
	}, [updateScrollHint, calendarKey]);

	const handleScrollHintClick = useCallback(() => {
		const el = calendarRef.current;
		if (!el) return;
		if (scrollHint === 'up') {
			el.scrollTo({ top: 0, behavior: 'smooth' });
		} else {
			el.scrollBy({ top: Math.round(el.clientHeight * 0.85), behavior: 'smooth' });
		}
	}, [scrollHint]);

	const handleBoundarySelect = useCallback((boundary: 'start' | 'end') => () => setActiveBoundary(boundary), []);

	const handleTimeSelect = useCallback(
		(hour: number) => () => {
			setTimeTouched(true);
			if (activeBoundary === 'start') setStartHour(hour);
			else setEndHour(hour);
		},
		[activeBoundary]
	);

	const maxSelectableDate = useMemo(() => {
		const today = new Date();
		if (startDate && !endDate) {
			const sixMonthsLater = addMonths(startDate, 6);
			return sixMonthsLater < today ? sixMonthsLater : today;
		}
		return today;
	}, [startDate, endDate]);

	const formatWeekDay = useCallback((day: string) => day.substring(0, 3), []);

	return (
		<div className={classNameMerger('bt_dateRangePicker border-dark-navy', onClose && 'bt_dateRangePicker--closable')}>
			{onClose && (
				<CloseModal
					aria-label='Close date filter'
					focusable
					role='button'
					tabIndex={0}
					onClick={onClose}
					className='bt_dateRangePicker_close svg-dark svg-dark-outline svg-dark-fill cursor_pointer'
				/>
			)}
			<div className='bt_dateRangePicker_header'>
				<div
					className='bt_dateRangePicker_header_timewrap'
					ref={dropdownRef}
				>
					<button
						type='button'
						className={`bt_dateRangePicker_header_timebtn${showTimePanel ? ' open' : ''}`}
						onClick={toggleTimePanel}
					>
						<span className='bt_dateRangePicker_header_timebtn_label'>
							{timeTouched ? `${formatHourLabel(startHour)} – ${formatHourLabel(endHour)}` : 'Time'}
						</span>
						<ChevronDown className='bt_dateRangePicker_header_timebtn_chevron svg-dark' />
					</button>

					{showTimePanel && (
						<div className='bt_dateRangePicker_timepanel bg-primary-600 theme-border-none'>
							<div className='bt_dateRangePicker_timepanel_toggle border-bottom-dark-navy'>
								<button
									type='button'
									className={`bt_dateRangePicker_timepanel_tab${activeBoundary === 'start' ? ' active' : ''}`}
									onClick={handleBoundarySelect('start')}
								>
									Start
								</button>
								<button
									type='button'
									className={`bt_dateRangePicker_timepanel_tab${activeBoundary === 'end' ? ' active' : ''}`}
									onClick={handleBoundarySelect('end')}
								>
									End
								</button>
							</div>

							<ul className='bt_dateRangePicker_timepanel_list scrollbar'>
								{TIME_OPTIONS.map((hour) => {
									const selected = activeBoundary === 'start' ? startHour === hour : endHour === hour;

									return (
										<li key={hour}>
											<button
												type='button'
												className={classNameMerger(
													'bt_dateRangePicker_timepanel_option bg-primary-500-hover',
													selected && 'active bg-primary-400'
												)}
												onClick={handleTimeSelect(hour)}
											>
												{formatHourLabel(hour)}
											</button>
										</li>
									);
								})}
							</ul>
						</div>
					)}
				</div>

				<div className='bt_dateRangePicker_header_inputs'>
					<div className='bt_dateRangePicker_header_inputs_field'>
						<SegmentedDateInput
							value={startDate}
							syncSignal={externalSync}
							ariaLabel='Start date'
							onChange={handleStartDate}
						/>
						<CalendarArrowIcon />
						<SegmentedDateInput
							value={endDate}
							syncSignal={externalSync}
							ariaLabel='End date'
							onChange={handleEndDate}
						/>
						<CalendarIcon
							variant='date-range'
							className='bt_dateRangePicker_header_inputs_icon svg-dark'
						/>
					</div>
				</div>

				<div className='bt_dateRangePicker_header_actions'>
					<Button
						type='button'
						className={classNameMerger('bt_dateRangePicker_applybtn', !canApply && 'disabled')}
						disabled={!canApply || loading}
						onClick={handleApply}
					>
						{loading ? (
							<span className='button-spinner bt_dateRangePicker_applybtn_spinner' />
						) : (
							<Typography
								as='span'
								variant='paragraph_2'
								className='bt_dateRangePicker_applybtn_label'
							>
								Apply Filter
							</Typography>
						)}
					</Button>

					<button
						type='button'
						className='bt_dateRangePicker_header_actions_clear'
						onClick={handleClear}
					>
						<Typography
							as='span'
							variant='paragraph_2'
						>
							Clear
						</Typography>
					</button>
				</div>
			</div>

			{validationError && (
				<p
					className='bt_dateRangePicker_error bt_input-error-message'
					role='alert'
				>
					{validationError}
				</p>
			)}

			<div className='bt_dateRangePicker_calendarwrap'>
				<div
					className='bt_dateRangePicker_calendar'
					ref={calendarRef}
				>
					<DatePicker
						key={calendarKey}
						onChange={handleRangeChange}
						startDate={startDate ?? undefined}
						endDate={endDate ?? undefined}
						openToDate={startDate ?? undefined}
						maxDate={maxSelectableDate}
						selectsRange
						monthsShown={2}
						inline
						shouldCloseOnSelect={false}
						formatWeekDay={formatWeekDay}
						renderCustomHeader={CustomHeader}
					/>
				</div>

				{scrollHint && (
					<div className='bt_dateRangePicker_calendar_scrollhint'>
						<button
							type='button'
							className='bt_dateRangePicker_calendar_scrollhint_btn'
							aria-label={scrollHint === 'up' ? 'Scroll calendar to top' : 'Scroll calendar down'}
							onClick={handleScrollHintClick}
						>
							<ChevronDown
								className={classNameMerger(
									'bt_dateRangePicker_calendar_scrollhint_icon',
									scrollHint === 'up' && 'bt_dateRangePicker_calendar_scrollhint_icon--up'
								)}
							/>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default DateRangePicker;
